import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { categoryToGlobalIssueResourceType, categoryToLocalIssueResourceType } from '../../../../../shared/issuesV2';
import { Action } from '../../../../../typings/permissions';
import usePermissions from '../../../../hooks/api/permissions/usePermissions';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import TeamsStore from '../../../../redux/teams';
import useHasPermission from '../../../../shared/permissions/useHasPermission';
import { IssueScopeKind } from '../../../../../shared/issuesV2/common';

/**
 * Helper hook to determine if the user potentially has the ability to ignore/unignore issues given the current scope, category, filters,
 * and their permissions.
 *
 * For example, if the user doesn't have the Org level permission, is on the global inbox, but doesn't have *any* ignore permissions,
 * then this would return false.
 *
 * @returns `true` if the user might have the ability to ignore/unignore issues.
 */
const useUserHasIgnorePermission = (): { isLoading: boolean; hasIgnorePermission: boolean } => {
  const { data: permissions, isLoading } = usePermissions();
  const hasPermission = useHasPermission();

  const issueCategory = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const selectedTeams = useSelector(TeamsStore.selectors.getTeamSelection);

  const resourceType = categoryToLocalIssueResourceType(issueCategory, scope.type);
  const resourceTypeAny = categoryToGlobalIssueResourceType(issueCategory, scope.type);

  return {
    isLoading,
    hasIgnorePermission: useMemo(() => {
      if (isLoading) {
        // If the user is still loading, we don't know for sure if they have any permissions, so we return true.
        // The BE will also do permissions checks so we don't need to worry about someone performing actions they
        // shouldn't be able to perform.
        return true;
      }

      const hasAnyIgnorePermission = hasPermission({
        action: Action.Resolve,
        resourceType: resourceTypeAny,
      });

      // If they have the org level permission, they can ignore/unignore anywhere.
      if (hasAnyIgnorePermission) {
        return true;
      }

      if (scope.type === IssueScopeKind.Project || scope.type === IssueScopeKind.ReleaseGroup) {
        // If they're looking at a project or release group, just check their permissions for that project/release group.
        return hasPermission({
          action: Action.Resolve,
          resourceType,
          projectId: scope.type === IssueScopeKind.Project ? scope.id : undefined,
          releaseGroupId: scope.type === IssueScopeKind.ReleaseGroup ? Number(scope.id) : undefined,
        });
      }

      // The last scope is that they're looking at the Global Issues Inbox.

      // If the selected team is `'null'`, then they would only be allowed to ignore if they have the global Any permission.
      // If they had that, then this function would have already returned true.
      if (selectedTeams === 'null') {
        return false;
      }

      // Whether they're filtering by specific teams or not, we can't know for sure if they have permissions or not. So
      // we just check to see if they have any ignore permissions for the resource type.
      return !permissions
        ? true
        : permissions.some(
          (permission) => permission.action === Action.Resolve && permission.resourceType === resourceType,
        );
    }, [isLoading, resourceType, resourceTypeAny, hasPermission, scope, selectedTeams, permissions]),
  };
};

export default useUserHasIgnorePermission;
